import 'styles/pages/mediakit.scss';
import '~scripts/main';

// Packages
import throttle from 'lodash/throttle';
import scrollTo from '~scripts/core/scrollTo';

// Swiper
import Swiper from 'swiper';
import { EffectFade, Navigation as SwiperNavigation, Pagination as SwiperPagination } from 'swiper/modules';
Swiper.use([EffectFade, SwiperNavigation, SwiperPagination]);

// Core
import { scrollAnimationProgress } from '~scripts/core/animations';
import { BREAKPOINTS } from '~scripts/core/breakpoints';

// Blocks
import Header from '~scripts/blocks/header';
import Navigation from '~scripts/blocks/navigation';

// Components
import '~scripts/components/modals';
import HandleForm from '~scripts/components/forms/forms';

// Breakpoints
const mediaSm = window.matchMedia(`(max-width: ${BREAKPOINTS.smMax})`);
const mediaMd = window.matchMedia(`(min-width: ${BREAKPOINTS.mdMin}) and (max-width: ${BREAKPOINTS.mdMax})`);
const mediaLg = window.matchMedia(`(min-width: ${BREAKPOINTS.lgMin})`);

// Variables
const windowHeight = window.innerHeight;

//
// HEADER
//
Header();

//
// NAVIGATION
//
Navigation();

//
// SWIPER
//
const swiperOn = {
  beforeInit: function (swiper) {
    const swiperEl = swiper.el;
    const swiperButtonPrev = swiperEl.querySelector('.swiper-button-prev');

    if (swiper.realIndex === 0) {
      swiperButtonPrev.style.opacity = 0;
    }
  },
  realIndexChange: function (swiper) {
    const swiperEl = swiper.el;
    const swiperButtonPrev = swiperEl.querySelector('.swiper-button-prev');

    if (swiper.realIndex === 0) {
      swiperButtonPrev.style.opacity = 0;
    } else {
      swiperButtonPrev.style.opacity = 1;
    }
  },
};

//
// NUMBERS
//
// Animation
let stopNumbersAnimation = [];

const handleNumbersAnimation = () => {
  const numbers = document.querySelectorAll('.js-numbers');

  if (mediaSm.matches) {
    stopNumbersAnimation.forEach((stop) => stop());
  } else {
    stopNumbersAnimation = Array.from(numbers, (number) => {
      return scrollAnimationProgress(number, {
        getStart(el) {
          const height = el.offsetHeight;
          const rect = el.getBoundingClientRect();
          return rect.top + window.scrollY - (windowHeight - height / 2);
        },
        getEnd(el, start) {
          const height = el.offsetHeight;
          return start + windowHeight + height * 2;
        },
      });
    });
  }
};

handleNumbersAnimation();
mediaSm.addEventListener('change', handleNumbersAnimation);

//
// CASES
//
const cases = document.querySelectorAll('.js-cases');

// Slider
const casesSliderOptionsBase = {
  effect: 'slide',
  pagination: {
    el: '.swiper-pagination',
    clickable: true,
  },
};
const casesSliderOptionsSm = {
  allowTouchMove: true,
  navigation: false,
  noSwiping: false,
  preventClicks: true,
  preventClicksPropagation: true,
  slidesPerGroup: 1,
  slidesPerView: 1,
  spaceBetween: 12,
};
const casesSliderOptionsMd = {
  allowTouchMove: true,
  navigation: false,
  noSwiping: false,
  preventClicks: true,
  preventClicksPropagation: true,
  slidesPerGroup: 2,
  slidesPerView: 2,
  spaceBetween: 12,
};
const casesSliderOptionsLg = {
  allowTouchMove: false,
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
  noSwiping: true,
  preventClicks: false,
  preventClicksPropagation: false,
  slidesPerGroup: 2,
  slidesPerView: 2,
  spaceBetween: 20,
  on: swiperOn,
};

const casesSliders = [];

const handleCasesSliderInit = () => {
  if (casesSliders.length) {
    casesSliders.forEach((caseSlider) => {
      if (caseSlider) {
        caseSlider.destroy();
      }
    });
  }

  const quantityToInitSlider = mediaSm.matches ? 2 : 5;

  const casesSliderOptionsMedia = mediaSm.matches
    ? casesSliderOptionsSm
    : mediaMd.matches
      ? casesSliderOptionsMd
      : mediaLg.matches
        ? casesSliderOptionsLg
        : {};
  const casesSliderOptions = Object.assign(casesSliderOptionsBase, casesSliderOptionsMedia);

  cases.forEach((casesList, casesListIndex) => {
    const casesWrapper = casesList.querySelector('.js-cases-wrapper') || null;
    const casesItems = casesList.querySelectorAll('.js-cases-item');

    const casesQuantity = casesList.querySelectorAll('.js-cases-item').length;

    if (casesQuantity >= quantityToInitSlider) {
      if (casesWrapper) {
        casesWrapper.classList.add('swiper-wrapper');
      }

      casesItems.forEach((caseItem) => {
        caseItem.classList.add('swiper-slide');
      });

      const casesSlider = new Swiper(casesList, casesSliderOptions);

      casesSliders.push(casesSlider);
    } else {
      if (casesWrapper) {
        casesWrapper.classList.remove('swiper-wrapper');
      }

      casesItems.forEach((caseItem) => {
        caseItem.classList.remove('swiper-slide');
      });
    }
  });
};

handleCasesSliderInit();

mediaSm.addEventListener('change', handleCasesSliderInit);
mediaMd.addEventListener('change', handleCasesSliderInit);
mediaLg.addEventListener('change', handleCasesSliderInit);

// Animation
let stopCasesItemsAnimation = [];

const handleCasesItemsAnimation = () => {
  if (mediaSm.matches) {
    stopCasesItemsAnimation.forEach((stop) => stop());
  } else {
    cases.forEach((casesItem) => {
      if (!casesItem.classList.contains('swiper-initialized')) {
        const casesItems = casesItem.querySelectorAll('.js-cases-item');

        stopCasesItemsAnimation = Array.from(casesItems, (caseItem) => {
          return scrollAnimationProgress(caseItem, {
            getStart(el) {
              const rect = el.getBoundingClientRect();
              return rect.top + window.scrollY - windowHeight;
            },
            getEnd(el, start) {
              const height = el.offsetHeight;
              return start + height * 0.725;
            },
          });
        });
      }
    });
  }
};

handleCasesItemsAnimation();
mediaSm.addEventListener('change', handleCasesItemsAnimation);
mediaMd.addEventListener('change', handleCasesItemsAnimation);
mediaLg.addEventListener('change', handleCasesItemsAnimation);

// Email
let stopCaseEmailAnimation = null;

const handleCaseEmailAnimation = () => {
  const caseEmail = document.getElementById('caseEmail');

  if (mediaSm.matches) {
    if (stopCaseEmailAnimation && typeof stopCaseEmailAnimation === 'function') {
      stopCaseEmailAnimation();
    }
  } else {
    stopCaseEmailAnimation = scrollAnimationProgress(caseEmail, {
      getStart(el) {
        const rect = el.getBoundingClientRect();
        return rect.top + window.scrollY - windowHeight;
      },
      getEnd(el, start) {
        const height = el.offsetHeight;
        return start + windowHeight + height;
      },
    });
  }
};

handleCaseEmailAnimation();
mediaSm.addEventListener('change', handleCaseEmailAnimation);

//
// PODCASTS
//
// Slider
const mediaMdMinNumber = parseInt(BREAKPOINTS.mdMin);
const mediaLgMinNumber = parseInt(BREAKPOINTS.lgMin);

const handlePodcastsFeedbacksSliderInit = () => {
  const podcastsFeedbacks = document.querySelectorAll('.js-podcasts-feedbacks');

  podcastsFeedbacks.forEach((podcastsFeedbacksList) => {
    new Swiper(podcastsFeedbacksList, {
      allowTouchMove: true,
      effect: 'slide',
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      noSwiping: false,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      slidesPerGroup: 1,
      slidesPerGroupAuto: true,
      slidesPerView: 'auto',
      spaceBetween: 12,
      breakpoints: {
        [mediaMdMinNumber]: {
          allowTouchMove: true,
          noSwiping: false,
          spaceBetween: 16,
        },
        [mediaLgMinNumber]: {
          allowTouchMove: false,
          noSwiping: true,
          spaceBetween: 20,
        },
      },
      on: swiperOn,
    });
  });
};

handlePodcastsFeedbacksSliderInit();

//
// REQUEST
//
function submitSubscribeMediakit(form) {
  const formData = new FormData(form);
  const body = Object.fromEntries(formData.entries());

  return fetch('/subscribe/mediakit', {
    method: 'post',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })
    .then((response) => {
      if (!response.ok) {
        return Promise.reject(`${response.status}: ${response.statusText}`);
      }

      return response;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
}

function initSubscribeMediakit(form) {
  if (!form) {
    return;
  }

  const сheckboxes = form.querySelectorAll('.js-checkboxes input[type="checkbox"]');
  const textarea = form.querySelector('.js-textarea');
  const buttonWrapper = form.querySelector('.js-submit-wrapper');
  const tooltip = buttonWrapper.querySelector('.js-tooltip');

  const getCheckboxesValue = () => {
    const servicesValues = Array.from(сheckboxes)
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.value)
      .join(' + ');

    textarea.textContent = servicesValues;
  };

  сheckboxes.forEach((checkbox) => {
    checkbox.addEventListener('change', getCheckboxesValue);
  });

  buttonWrapper.addEventListener('mouseleave', () => {
    if (!tooltip.classList.contains('form-tooltip--hidden')) {
      tooltip.classList.add('form-tooltip--hidden');
    }
  });

  const handleForm = new HandleForm(form);

  const handleSubmit = throttle(() => {
    if (handleForm.getSending()) {
      return;
    }

    if (!handleForm.checkValidity()) {
      if (textarea.value === '') {
        tooltip.classList.remove('form-tooltip--hidden');
      }
      return;
    }

    handleForm.setSending(true);
    handleForm.disableSubmit();

    function success() {
      const rect = form.getBoundingClientRect();
      const y = rect.top + window.scrollY - 250;
      window.scrollTo(0, y);

      handleForm.handleSuccess();
    }

    function error(error) {
      handleForm.handleError(error);
    }

    submitSubscribeMediakit(form)
      .then(success, error)
      .finally(() => {
        handleForm.setSending(false);
        handleForm.enableSubmit();
      });
  }, 100);

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    handleSubmit();
  });
}

const subscribeMediakitForms = document.querySelectorAll('.js-subscribe-mediakit-form');

subscribeMediakitForms.forEach((subscribeMediakitForm) => {
  initSubscribeMediakit(subscribeMediakitForm);
});
